import { useState } from "react";
import { Form, Button, Table, Row, Col } from "react-bootstrap";
import { AppLoader } from "../../../../Components/common/AppLoader/AppLoader";
import { MdDelete, MdEdit, MdSchool } from "react-icons/md";
import { VideoCard } from "../../../../Components/ui/VideoCard/VideoCard";
import { mapCmeToVideoCardProps } from "../../utils/digitalCmeHelpers";

const EMPTY_FORM = {
  doctor_id: "",
  cme_title: "",
  context_tags: "",
  medical_specialty_ids: "",
  media_link: "",
  short_description: "",
  long_description: "",
  duration_minutes: "",
};

export const DigitalCmeScreen = ({
  items,
  medicalSpecialties,
  loading,
  saving,
  onSave,
  onDelete,
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [referenceFiles, setReferenceFiles] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [removedReferenceIds, setRemovedReferenceIds] = useState([]);
  const [existingReferences, setExistingReferences] = useState([]);

  const selectedSpecIds = form.medical_specialty_ids
    ? form.medical_specialty_ids.split(",").map((s) => s.trim()).filter(Boolean)
    : [];

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setReferenceFiles([]);
    setEditingId(null);
    setRemovedReferenceIds([]);
    setExistingReferences([]);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSpecToggle = (id) => {
    const strId = String(id);
    const updated = selectedSpecIds.includes(strId)
      ? selectedSpecIds.filter((x) => x !== strId)
      : [...selectedSpecIds, strId];
    handleChange("medical_specialty_ids", updated.join(","));
  };

  const startEdit = (item) => {
    setEditingId(item.digital_cme_id);
    setForm({
      doctor_id: item.doctor_id ? String(item.doctor_id) : "",
      cme_title: item.cme_title || "",
      context_tags: item.context_tags || "",
      medical_specialty_ids: (item.specialties || [])
        .map((s) => String(s.medical_specialty_id))
        .join(","),
      media_link: item.media_link || "",
      short_description: item.short_description || "",
      long_description: item.long_description || "",
      duration_minutes: item.duration_minutes != null ? String(item.duration_minutes) : "",
    });
    setReferenceFiles([]);
    setRemovedReferenceIds([]);
    setExistingReferences(item.references || []);
  };

  const toggleRemoveFile = (id) => {
    setRemovedReferenceIds((prev) =>
      prev.includes(String(id))
        ? prev.filter((x) => x !== String(id))
        : [...prev, String(id)]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ok = await onSave(
      form,
      referenceFiles,
      editingId,
      removedReferenceIds
    );
    if (ok) resetForm();
  };

  if (loading) {
    return <AppLoader message="Loading digital CME…" variant="portal" />;
  }

  return (
    <div className="digital-cme-page">
      <div className="em-section-card">
        <h4 className="em-section-title">
          <MdSchool className="media-gallery-title-icon" />
          Digital CME
        </h4>

        <Form onSubmit={handleSubmit}>
          <Row className="em-row">
            <Form.Group as={Col} md="6" controlId="cmeDoctorId">
              <Form.Label>Doctor ID</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={form.doctor_id}
                onChange={(e) => handleChange("doctor_id", e.target.value)}
                placeholder="Enter doctor ID"
              />
            </Form.Group>
            <Form.Group as={Col} md="6" controlId="cmeDuration">
              <Form.Label>Duration (minutes)</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={form.duration_minutes}
                onChange={(e) => handleChange("duration_minutes", e.target.value)}
                placeholder="e.g. 20"
              />
            </Form.Group>
          </Row>

          <Row className="em-row">
            <Form.Group as={Col} md="12" controlId="cmeTitle">
              <Form.Label>Digital CME Title</Form.Label>
              <Form.Control
                type="text"
                value={form.cme_title}
                onChange={(e) => handleChange("cme_title", e.target.value)}
                placeholder="Enter CME title"
                required
              />
            </Form.Group>
          </Row>

          <Row className="em-row">
            <Form.Group as={Col} md="6" controlId="cmeContext">
              <Form.Label>Context</Form.Label>
              <Form.Control
                type="text"
                value={form.context_tags}
                onChange={(e) => handleChange("context_tags", e.target.value)}
                placeholder="#cardiology #cme"
              />
              <span className="em-field-hint">Use hashtags e.g. #cardiology #research</span>
            </Form.Group>
            <Form.Group as={Col} md="6" controlId="cmeMediaLink">
              <Form.Label>Media Link (URL)</Form.Label>
              <Form.Control
                type="url"
                value={form.media_link}
                onChange={(e) => handleChange("media_link", e.target.value)}
                placeholder="https://..."
              />
            </Form.Group>
          </Row>

          <Row className="em-row">
            <Form.Group as={Col} md="12" controlId="cmeSpecialties">
              <Form.Label>Medical Speciality (multiple)</Form.Label>
              <div className="digital-cme-spec-list">
                {medicalSpecialties.length === 0 ? (
                  <span className="em-field-hint">No specialties loaded.</span>
                ) : (
                  medicalSpecialties.map((s) => (
                    <Form.Check
                      key={s.medical_specialty_id}
                      type="checkbox"
                      id={`cme-spec-${s.medical_specialty_id}`}
                      label={s.medical_specialty}
                      checked={selectedSpecIds.includes(String(s.medical_specialty_id))}
                      onChange={() => handleSpecToggle(s.medical_specialty_id)}
                    />
                  ))
                )}
              </div>
            </Form.Group>
          </Row>

          <Row className="em-row">
            <Form.Group as={Col} md="12" controlId="cmeShortDesc">
              <Form.Label>Short Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.short_description}
                onChange={(e) => handleChange("short_description", e.target.value)}
                placeholder="Brief summary shown on the card"
              />
            </Form.Group>
          </Row>

          <Row className="em-row">
            <Form.Group as={Col} md="12" controlId="cmeLongDesc">
              <Form.Label>Long Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={form.long_description}
                onChange={(e) => handleChange("long_description", e.target.value)}
                placeholder="Full CME description"
              />
            </Form.Group>
          </Row>

          <Row className="em-row">
            <Form.Group as={Col} md="6" controlId="cmeReferencePdfs">
              <Form.Label>Reference Material (PDF)</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf,application/pdf"
                multiple
                className="em-file-input"
                onChange={(e) => setReferenceFiles(Array.from(e.target.files || []))}
              />
              <span className="em-field-hint">Upload one or more PDF reference documents</span>
            </Form.Group>
          </Row>

          {editingId && existingReferences.length > 0 && (
            <div className="news-existing-files">
              <div className="news-existing-files__group">
                <p className="news-existing-files__label">Existing PDFs (click to remove)</p>
                <div className="news-existing-files__list">
                  {existingReferences.map((ref) => {
                    const marked = removedReferenceIds.includes(String(ref.reference_id));
                    return (
                      <button
                        key={ref.reference_id}
                        type="button"
                        className={`news-existing-files__item${marked ? " news-existing-files__item--removed" : ""}`}
                        onClick={() => toggleRemoveFile(ref.reference_id)}
                      >
                        {ref.file_path}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {referenceFiles.length > 0 && (
            <p className="em-field-hint">{referenceFiles.length} PDF(s) selected</p>
          )}

          <div className="media-gallery-form__actions">
            {editingId && (
              <Button type="button" variant="outline-secondary" onClick={resetForm} disabled={saving}>
                Cancel Edit
              </Button>
            )}
            <Button type="submit" variant="primary" className="btn-submit" disabled={saving}>
              {saving ? "Saving…" : editingId ? "Update CME" : "Save CME"}
            </Button>
          </div>
        </Form>
      </div>

      {items.length > 0 && (
        <div className="em-section-card">
          <h4 className="em-section-title">Saved Digital CME</h4>
          <div className="hm-cme-scroll">
            <div className="hm-cme-scroll__track">
              {items.map((item) => (
                <div key={item.digital_cme_id} className="hm-cme-scroll__item">
                  <VideoCard {...mapCmeToVideoCardProps(item)} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="em-section-card media-gallery-table-card">
        <h4 className="em-section-title">Digital CME Records</h4>
        {items.length === 0 ? (
          <p className="hm-card-text hm-card-text--muted">No digital CME added yet.</p>
        ) : (
          <div className="table-responsive">
            <Table striped bordered hover size="sm" className="media-gallery-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Title</th>
                  <th>Doctor</th>
                  <th>Duration</th>
                  <th>Specialties</th>
                  <th>PDFs</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.digital_cme_id}>
                    <td>{item.digital_cme_id}</td>
                    <td>{item.cme_title}</td>
                    <td>{item.doctor_id || "—"}</td>
                    <td>{item.duration_minutes ? `${item.duration_minutes} min` : "—"}</td>
                    <td>
                      {(item.specialties || []).map((s) => s.medical_specialty).join(", ") || "—"}
                    </td>
                    <td>{item.references?.length || 0}</td>
                    <td>
                      <div className="media-gallery-table__actions">
                        <button
                          type="button"
                          className="media-gallery-table__btn"
                          onClick={() => startEdit(item)}
                          aria-label="Edit"
                        >
                          <MdEdit />
                        </button>
                        <button
                          type="button"
                          className="media-gallery-table__btn media-gallery-table__btn--danger"
                          onClick={() => onDelete(item.digital_cme_id)}
                          aria-label="Delete"
                          disabled={saving}
                        >
                          <MdDelete />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DigitalCmeScreen;
